import React, { useEffect, useState } from "react";
import {
  CHOICE_FORM_TYPES,
  DEFAULT_FORM_FIELDS,
  FORM_TYPES,
  ID_LABEL,
  QUESTION_FORM_TYPES,
  ensureSpreadsheet,
  findIdFieldIndex,
  readFormFields,
  readQrSettings,
  spreadsheetUrl,
  writeFormFields,
} from "../api/sheets";
import {
  checkLinkedFormAccessible,
  createDetachedResponsesSheet,
  createFormFromSchemaAction,
  detachForm,
  ensureResponsesSheet,
  ensureFormTemplate,
  formEditUrl,
  importExistingFormUrl,
  linkExistingFormById,
  linkFirstFormInFolder,
  pruneLinkedForm,
  publishLinkedForm,
  restoreFormUrlBackup,
  setThemedTemplateFromPicked,
  syncToForm,
} from "../api/forms";
import { pickGoogleForm } from "../auth/googlePicker";
import PickerCookieHint from "../components/PickerCookieHint";
import { useViewWidth } from "../components/ViewWidthSlider";
import EmailBackendInstructions from "../components/EmailBackendInstructions";
import { useT } from "../i18n";
import { FormField } from "../types/sheets";

const CHOICE_TYPES = new Set(CHOICE_FORM_TYPES);
const QUESTION_TYPES = new Set(QUESTION_FORM_TYPES);

interface EditorRow {
  label: string;
  type: string;
  values: string;
  description: string;
}

function toEditorFields(fields: readonly FormField[]): EditorRow[] {
  return fields.map((f) => ({
    label: f.label,
    type: f.type,
    values: Array.isArray(f.values) ? f.values.join("|") : String(f.values ?? ""),
    description: f.description ?? "",
  }));
}

function fromEditorFields(rows: EditorRow[]): FormField[] {
  return rows
    .map((r) => ({
      label: String(r.label || "").trim(),
      type: FORM_TYPES.includes(r.type) ? r.type : "text",
      values: CHOICE_TYPES.has(r.type)
        ? String(r.values || "").split("|").map((v) => v.trim()).filter((v) => v.length > 0)
        : [],
      description: String(r.description || ""),
    }))
    .filter((f) => f.label.length > 0);
}

interface QrState {
  formId: string;
  formUrl: string;
  formLocked: boolean;
  formIdBackup: string;
  formUrlBackup: string;
  responsesSheetId: string;
  setupComplete: string;
}

export default function FormSettingsView() {
  const t = useT();
  const { maxWidth, slider } = useViewWidth("viewWidth.formSettings");
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [rows, setRows] = useState<EditorRow[]>(toEditorFields(DEFAULT_FORM_FIELDS));
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(true);
  const [snapshot, setSnapshot] = useState<EditorRow[] | null>(null);
  const [qr, setQr] = useState<QrState>({ formId: "", formUrl: "", formLocked: false, formIdBackup: "", formUrlBackup: "", responsesSheetId: "", setupComplete: "" });
  const [showBackendDialog, setShowBackendDialog] = useState(false);
  const [status, setStatus] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const notify = (text: string, tone = "success") => { setStatus(text); setStatusTone(tone); };
  const notifyError = (text: string) => { setStatus(text); setStatusTone("error"); };

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const sid = await ensureSpreadsheet();
        setSpreadsheetId(sid);
        const [fields, qrCurrent] = await Promise.all([readFormFields(sid), readQrSettings(sid)]);
        setRows(toEditorFields(fields));
        let effectiveQr = qrCurrent;
        if (qrCurrent.formId) {
          try {
            const { accessible } = await checkLinkedFormAccessible(sid);
            if (!accessible) {
              if (window.confirm(t("formSettings.confirmPruneInaccessible"))) {
                effectiveQr = await pruneLinkedForm(sid);
                notify(t("formSettings.linkPruned"), "info");
              } else {
                notify(t("formSettings.linkKept"), "info");
              }
            }
          } catch (e) { console.warn("checkLinkedFormAccessible:", e); }
        }
        setQr({ formId: effectiveQr.formId || "", formUrl: effectiveQr.formUrl || "", formLocked: Boolean(effectiveQr.formLocked), formIdBackup: effectiveQr.formIdBackup || "", formUrlBackup: effectiveQr.formUrlBackup || "", responsesSheetId: effectiveQr.responsesSheetId || "", setupComplete: effectiveQr.setupComplete || "" });
      } catch (e) {
        notifyError(`${t("common.error")}: ${(e as Error).message}`);
      } finally {
        setBusy(false);
      }
    })();
    ensureFormTemplate().catch((e) => console.warn("ensureFormTemplate:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshQr() {
    if (!spreadsheetId) return;
    const fresh = await readQrSettings(spreadsheetId);
    setQr({ formId: fresh.formId || "", formUrl: fresh.formUrl || "", formLocked: Boolean(fresh.formLocked), formIdBackup: fresh.formIdBackup || "", formUrlBackup: fresh.formUrlBackup || "", responsesSheetId: fresh.responsesSheetId || "", setupComplete: fresh.setupComplete || "" });
  }

  async function handleCreateResponsesSheet() {
    if (!spreadsheetId) return;
    // Reused-by-ID form: adopt the sheet it's already attached to; never create silently.
    if (qr.formLocked) {
      setBusy(true);
      try {
        const result = await ensureResponsesSheet(spreadsheetId);
        await refreshQr();
        if (result.responsesSheetId) {
          notify(t("formSettings.responsesSheetAdopted"));
        } else {
          // No sheet attached to the reused form — offer to create one (optional).
          if (!window.confirm(t("formSettings.confirmCreateDetachedSheet"))) return;
          await createDetachedResponsesSheet(spreadsheetId);
          await refreshQr();
          notify(t("formSettings.detachedSheetCreated"), "info");
        }
      } catch (e) {
        notifyError(t("formSettings.responsesSheetError", { msg: (e as Error).message }));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!window.confirm(t("formSettings.confirmCreateResponsesSheet"))) return;
    setBusy(true);
    notify(t("formSettings.creatingResponsesSheet"), "info");
    try {
      const result = await ensureResponsesSheet(spreadsheetId);
      await refreshQr();
      if (result.alreadyLinked) {
        notify(t("formSettings.responsesSheetAlreadyLinked"));
      } else {
        notify(t("formSettings.responsesSheetCreated"), "info");
      }
    } catch (e) {
      notifyError(t("formSettings.responsesSheetError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handlePublishForm() {
    if (!spreadsheetId) return;
    if (!window.confirm(t("formSettings.confirmPublish"))) return;
    setBusy(true);
    notify(t("formSettings.publishing"), "info");
    try {
      await publishLinkedForm(spreadsheetId);
      notify(t("formSettings.published"));
    } catch (e) {
      notifyError(t("formSettings.publishError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  function startEdit() {
    if (!window.confirm(t("formSettings.confirmEdit"))) return;
    setSnapshot(rows);
    setLocked(false);
  }

  function cancelEdit() {
    if (snapshot) setRows(snapshot);
    setSnapshot(null);
    setLocked(true);
  }

  function updateEditorRow(i: number, patch: Partial<EditorRow>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((rs) => [...rs, { label: "", type: "text", values: "", description: "" }]);
  }
  function removeRow(i: number) {
    setRows((rs) => rs.filter((_, idx) => idx !== i));
  }
  function moveRow(i: number, dir: number) {
    setRows((rs) => {
      const j = i + dir;
      if (j < 0 || j >= rs.length) return rs;
      const next = [...rs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }
  function reorderRow(from: number, to: number) {
    setRows((rs) => {
      if (from === to || from < 0 || to < 0 || from >= rs.length || to >= rs.length) return rs;
      const next = [...rs];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }
  function handleDrop(target: number) {
    if (dragIndex !== null) reorderRow(dragIndex, target);
    setDragIndex(null);
    setDropIndex(null);
  }

  function validate(normalized: FormField[]): string | null {
    if (normalized.length === 0) return t("formSettings.noFields");
    if (normalized[0]?.type !== "title") return t("formSettings.titleFirstRequired");
    const questionFields = normalized.filter((f) => QUESTION_TYPES.has(f.type));
    if (questionFields.length === 0) return t("formSettings.noQuestions");
    if (findIdFieldIndex(questionFields) < 0) return t("formSettings.idFieldMissing", { label: ID_LABEL });
    for (const f of normalized) {
      if (CHOICE_TYPES.has(f.type) && f.values.length === 0) {
        return t("formSettings.choiceValuesMissing", { label: f.label });
      }
    }
    return null;
  }

  async function handleSaveSchema() {
    if (!spreadsheetId || locked) return;
    const normalized = fromEditorFields(rows);
    const err = validate(normalized);
    if (err) { notifyError(err); return; }
    if (!window.confirm(t("formSettings.confirmSaveSchema"))) return;
    setBusy(true);
    try {
      await writeFormFields(spreadsheetId, normalized);
      setRows(toEditorFields(normalized));
      setSnapshot(null);
      setLocked(true);
      notify(t("formSettings.schemaSaved"));
    } catch (e) {
      notifyError(t("formSettings.schemaSaveError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handleSync() {
    if (!spreadsheetId) return;
    if (!window.confirm(t("formSettings.confirmSyncExisting"))) return;
    setBusy(true);
    notify(t("formSettings.syncing"), "info");
    try {
      await syncToForm(spreadsheetId);
      await refreshQr();
      notify(t("formSettings.syncedExisting"));
    } catch (e) {
      notifyError(t("formSettings.syncError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handleCreateFromSchema() {
    if (!spreadsheetId) return;
    if (!window.confirm(t("formSettings.confirmCreateFromSchema"))) return;
    setBusy(true);
    notify(t("formSettings.creating"), "info");
    try {
      const result = await createFormFromSchemaAction(spreadsheetId);
      await refreshQr();
      notify(t("formSettings.createdFromSchema", { url: result.formUrl }));
    } catch (e) {
      notifyError(t("formSettings.createError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handleImportUrl() {
    if (!spreadsheetId) return;
    const hasExisting = Boolean(qr.formUrl || qr.formId);
    const prompt = hasExisting ? t("formSettings.replacePrompt", { current: qr.formUrl || "(none)" }) : t("formSettings.importPrompt");
    const entered = window.prompt(prompt, qr.formUrl || "");
    if (entered == null) return;
    const url = entered.trim();
    if (!url) return;
    if (hasExisting && !window.confirm(t("formSettings.confirmReplace"))) return;
    setBusy(true);
    try {
      await importExistingFormUrl(spreadsheetId, url, { confirmReplace: hasExisting });
      await refreshQr();
      notify(hasExisting ? t("formSettings.urlReplaced") : t("formSettings.urlImported"));
    } catch (e) {
      notifyError(t("formSettings.importError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handleScanFolder() {
    if (!spreadsheetId) return;
    if (!window.confirm(t("formSettings.confirmScanFolder"))) return;
    setBusy(true);
    try {
      const result = await linkFirstFormInFolder(spreadsheetId);
      await refreshQr();
      notify(t("formSettings.scanLinked", { name: result.name }));
    } catch (e) {
      notifyError(t("formSettings.scanError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handleDetach() {
    if (!spreadsheetId) return;
    if (!window.confirm(t("formSettings.confirmDetach"))) return;
    setBusy(true);
    try {
      await detachForm(spreadsheetId);
      await refreshQr();
      notify(t("formSettings.detached"));
    } catch (e) {
      notifyError(t("formSettings.detachError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handleOpenTemplate() {
    setBusy(true);
    try {
      const { formId, created } = await ensureFormTemplate();
      window.open(formEditUrl(formId), "_blank", "noopener");
      notify(t(created ? "formSettings.templateCreated" : "formSettings.templateOpened"));
    } catch (e) {
      notifyError(t("formSettings.templateError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handleImportThemedTemplate() {
    setBusy(true);
    try {
      const picked = await pickGoogleForm(t("formSettings.pickTemplateTitle"));
      if (!picked) return; // user cancelled
      if (!window.confirm(t("formSettings.confirmThemedTemplate", { name: picked.name }))) return;
      const { formId } = await setThemedTemplateFromPicked(picked.id);
      notify(t("formSettings.themedTemplateSet", { name: picked.name }));
      window.open(formEditUrl(formId), "_blank", "noopener");
    } catch (e) {
      notifyError(t("formSettings.themedTemplateError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handleReuseExistingForm() {
    if (!spreadsheetId) return;
    setBusy(true);
    try {
      const picked = await pickGoogleForm(t("formSettings.pickReuseTitle"));
      if (!picked) return; // cancelled
      const hasExisting = Boolean(qr.formUrl || qr.formId);
      if (!window.confirm(t("formSettings.confirmReuseForm", { name: picked.name }))) return;
      if (hasExisting && !window.confirm(t("formSettings.confirmReplace"))) return;
      const result = await linkExistingFormById(spreadsheetId, picked.id, { confirmReplace: hasExisting });
      await refreshQr();
      notify(result.hasIdQuestion ? t("formSettings.reuseLinked", { name: picked.name }) : t("formSettings.reuseLinkedNoId", { name: picked.name }), result.hasIdQuestion ? "success" : "info");
    } catch (e) {
      notifyError(t("formSettings.reuseError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handleRestoreBackup() {
    if (!spreadsheetId) return;
    if (!window.confirm(t("formSettings.confirmRestore"))) return;
    setBusy(true);
    try {
      await restoreFormUrlBackup(spreadsheetId);
      await refreshQr();
      notify(t("formSettings.restored"));
    } catch (e) {
      notifyError(t("formSettings.restoreError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  const inputCls = "w-full border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed";
  const hasLinkedForm = Boolean(qr.formId || qr.formUrl);
  const hasBackup = Boolean(qr.formIdBackup || qr.formUrlBackup);
  const hasFormId = Boolean(qr.formId);
  const hasResponsesSheet = Boolean(qr.responsesSheetId);
  const backendSetupComplete = Boolean(qr.setupComplete);
  const canSetUpBackend = hasFormId && hasResponsesSheet && !backendSetupComplete;
  const hasSchemaQuestions = rows.some((r) => QUESTION_TYPES.has(r.type) && String(r.label || "").trim().length > 0);
  const openInNewTab = (url: string) => window.open(url, "_blank", "noopener");

  interface FormAction {
    key: string; icon: string; label: string; explain: string;
    enabled: boolean; missing: string; onClick: () => void;
  }

  const formActions: FormAction[] = [
    { key: "createFromSchema", icon: "fa-wand-magic-sparkles", label: t("formSettings.createFromSchema"), explain: t("formSettings.explain.createFromSchema"), enabled: !hasLinkedForm && hasSchemaQuestions, missing: !hasSchemaQuestions ? t("formSettings.need.schema") : t("formSettings.need.notLinked"), onClick: handleCreateFromSchema },
    { key: "scanFolder", icon: "fa-folder-magnifying-glass", label: t("formSettings.scanFolder"), explain: t("formSettings.explain.scanFolder"), enabled: !hasLinkedForm, missing: t("formSettings.need.notLinked"), onClick: handleScanFolder },
    { key: "reuseExistingForm", icon: "fa-arrows-rotate", label: hasLinkedForm ? t("formSettings.reuseReplace") : t("formSettings.reuseExistingForm"), explain: t("formSettings.explain.reuseExistingForm"), enabled: Boolean(spreadsheetId), missing: t("formSettings.need.linkedForm"), onClick: handleReuseExistingForm },
    { key: "importUrl", icon: hasLinkedForm ? "fa-arrow-right-arrow-left" : "fa-file-import", label: hasLinkedForm ? t("formSettings.replaceUrl") : t("formSettings.importExistingUrl"), explain: t("formSettings.explain.importUrl"), enabled: Boolean(spreadsheetId), missing: t("formSettings.need.linkedForm"), onClick: handleImportUrl },
    { key: "sync", icon: "fa-cloud-arrow-up", label: t("formSettings.sync"), explain: t("formSettings.explain.sync"), enabled: hasFormId && hasSchemaQuestions && !qr.formLocked, missing: qr.formLocked ? t("formSettings.need.notLocked") : !hasFormId ? t("formSettings.need.linkedForm") : t("formSettings.need.schema"), onClick: handleSync },
    { key: "openGoogleForm", icon: "fa-up-right-from-square", label: t("formSettings.openGoogleForm"), explain: t("formSettings.explain.openGoogleForm"), enabled: hasFormId, missing: t("formSettings.need.linkedForm"), onClick: () => openInNewTab(formEditUrl(qr.formId)) },
    { key: "createResponsesSheet", icon: "fa-table", label: qr.formLocked ? (hasResponsesSheet ? t("formSettings.useLinkedResponsesSheet") : t("formSettings.checkResponsesSheet")) : t("formSettings.createResponsesSheet"), explain: qr.formLocked ? t("formSettings.explain.reusedResponsesSheet") : t("formSettings.explain.createResponsesSheet"), enabled: hasFormId, missing: t("formSettings.need.linkedForm"), onClick: handleCreateResponsesSheet },
    { key: "publishForm", icon: "fa-bullhorn", label: t("formSettings.publishForm"), explain: t("formSettings.explain.publishForm"), enabled: hasFormId && !qr.formLocked, missing: qr.formLocked ? t("formSettings.need.notLocked") : t("formSettings.need.linkedForm"), onClick: handlePublishForm },
    { key: "openResponsesSheet", icon: "fa-up-right-from-square", label: t("formSettings.openResponsesSheet"), explain: t("formSettings.explain.openResponsesSheet"), enabled: hasResponsesSheet, missing: t("formSettings.need.responsesSheet"), onClick: () => openInNewTab(spreadsheetUrl(qr.responsesSheetId)) },
    { key: "detach", icon: "fa-link-slash", label: t("formSettings.detach"), explain: t("formSettings.explain.detach"), enabled: hasLinkedForm, missing: t("formSettings.need.linkedForm"), onClick: handleDetach },
    { key: "restoreBackup", icon: "fa-rotate-left", label: t("formSettings.restoreBackup"), explain: t("formSettings.explain.restoreBackup"), enabled: hasBackup, missing: t("formSettings.need.backup"), onClick: handleRestoreBackup },
  ];

  return (
    <>
    {busy && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 select-none">
          <div className="relative">
            <img
              src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`}
              alt="MeW"
              className="w-16 h-16 opacity-90"
            />
            <span className="absolute inset-0 -m-2.5 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
          </div>
          <p className="text-sm text-gray-500 tracking-wide">{t("common.processing")}</p>
        </div>
      </div>
    )}
    {showBackendDialog && spreadsheetId && (
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full my-8 p-6 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <i className="fa-solid fa-envelope-circle-check text-indigo-600" aria-hidden="true" />
              {t("formSettings.emailBackendDialogTitle")}
            </h2>
            <button
              type="button"
              onClick={() => setShowBackendDialog(false)}
              className="text-gray-400 hover:text-gray-600"
              title={t("common.cancel")}
              aria-label={t("common.cancel")}
            >
              <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
            </button>
          </div>
          <EmailBackendInstructions
            dataSpreadsheetId={spreadsheetId}
            formId={qr.formId}
            responsesSheetId={qr.responsesSheetId}
          />
        </div>
      </div>
    )}
    <div className="flex justify-end px-4 pt-2">{slider}</div>
    <div className="mx-auto p-4 space-y-4" style={{ maxWidth }}>
      <h2 className="font-bold text-xl flex items-center gap-2 text-gray-800">
        <i className="fa-solid fa-list-check text-indigo-600" aria-hidden="true" />
        {t("formSettings.title")}
      </h2>
      <p className="text-sm text-gray-600">{t("formSettings.intro")}</p>

      <div className="border rounded-md p-3 space-y-2 bg-gray-50">
        <div className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <i className="fa-solid fa-link text-gray-500" aria-hidden="true" />
          <span>{t("formSettings.linkedFormHeader")}</span>
        </div>
        {hasLinkedForm ? (
          <>
            <div className="text-xs text-gray-700 break-all bg-white border rounded-md px-2 py-1.5 font-mono">{qr.formUrl || "(formId only — no URL stored)"}</div>
            {qr.formLocked && (
              <div className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded-md px-2 py-1.5 flex items-start gap-1.5">
                <i className="fa-solid fa-lock mt-0.5" aria-hidden="true" />
                <span>{t("formSettings.reusedReadOnly")}</span>
              </div>
            )}
            {qr.formLocked && !hasResponsesSheet && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5 flex flex-col gap-1.5">
                <span className="flex items-start gap-1.5">
                  <i className="fa-solid fa-triangle-exclamation mt-0.5" aria-hidden="true" />
                  <span>{t("formSettings.noSheetAttached")}</span>
                </span>
                <button type="button" onClick={handleCreateResponsesSheet} disabled={busy} className="self-start inline-flex items-center gap-1.5 border border-amber-300 bg-white rounded-md px-2.5 py-1 hover:bg-amber-100 disabled:opacity-50">
                  <i className="fa-solid fa-table" aria-hidden="true" />
                  <span>{t("formSettings.createResponsesSheetOptional")}</span>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5">{t("formSettings.notLinkedYet")}</div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-gray-700 border-b">
                <th className="py-2 pr-2 w-8" aria-label="Check" />
                <th className="py-2 pr-2 w-56">{t("formSettings.colAction")}</th>
                <th className="py-2 pr-2">{t("formSettings.colExplanation")}</th>
              </tr>
            </thead>
            <tbody>
              {formActions.map((a) => (
                <tr key={a.key} className="border-b align-top">
                  <td className="py-2 pr-2">
                    <i className={a.enabled ? "fa-solid fa-circle-check text-green-600" : "fa-solid fa-circle-xmark text-gray-300"} title={a.enabled ? "" : a.missing} aria-hidden="true" />
                  </td>
                  <td className="py-2 pr-2">
                    <button type="button" onClick={a.onClick} disabled={busy || !spreadsheetId || !a.enabled}
                      className="inline-flex items-center gap-1.5 border border-gray-300 bg-white rounded-md px-3 py-1.5 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed w-full">
                      <i className={`fa-solid ${a.icon}`} aria-hidden="true" />
                      <span>{a.label}</span>
                    </button>
                  </td>
                  <td className="py-2 pr-2 text-xs text-gray-600">
                    {a.explain}
                    {!a.enabled && <div className="text-amber-700 mt-0.5">{a.missing}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasLinkedForm && <div className="text-xs text-gray-500">{t("formSettings.linkedFormSafe")}</div>}
        <PickerCookieHint />
      </div>

      <div className="border rounded-md p-3 space-y-2 bg-gray-50">
        <div className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <i className="fa-solid fa-envelope-circle-check text-gray-500" aria-hidden="true" />
          <span>{t("formSettings.emailBackendDialogTitle")}</span>
        </div>
        <div className="text-xs text-gray-600">{t("formSettings.emailBackendExplain")}</div>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <button
            type="button"
            onClick={() => setShowBackendDialog(true)}
            disabled={busy || !canSetUpBackend}
            className="inline-flex items-center gap-1.5 border border-gray-300 bg-white rounded-md px-3 py-1.5 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="fa-solid fa-envelope-circle-check" aria-hidden="true" />
            <span>{t("formSettings.emailBackend")}</span>
          </button>
          {backendSetupComplete ? (
            <span className="text-green-700 inline-flex items-center gap-1.5">
              <i className="fa-solid fa-circle-check" aria-hidden="true" />
              {t("formSettings.emailBackendDone")}
            </span>
          ) : !hasFormId || !hasResponsesSheet ? (
            <span className="text-amber-700">{t("formSettings.emailBackendNeedForm")}</span>
          ) : null}
        </div>
      </div>

      <div className="border rounded-md p-3 space-y-2 bg-gray-50">
        <div className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <i className="fa-solid fa-clone text-gray-500" aria-hidden="true" />
          <span>{t("formSettings.templateHeader")}</span>
        </div>
        <div className="text-xs text-gray-600">{t("formSettings.templateHelp")}</div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
          <button type="button" onClick={handleOpenTemplate} disabled={busy} className="inline-flex items-center gap-1.5 border border-gray-300 bg-white rounded-md px-3 py-1.5 hover:bg-gray-100 disabled:opacity-50">
            <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
            <span>{t("formSettings.editTemplate")}</span>
          </button>
          <button type="button" onClick={handleImportThemedTemplate} disabled={busy} className="inline-flex items-center gap-1.5 border border-gray-300 bg-white rounded-md px-3 py-1.5 hover:bg-gray-100 disabled:opacity-50">
            <i className="fa-solid fa-palette" aria-hidden="true" />
            <span>{t("formSettings.importThemedTemplate")}</span>
          </button>
        </div>
      </div>

      {locked && (
        <div className="text-sm rounded-md px-3 py-2 flex items-center gap-2 border text-amber-800 bg-amber-50 border-amber-100">
          <i className="fa-solid fa-lock mt-0.5" aria-hidden="true" />
          <span>{t("formSettings.locked")}</span>
        </div>
      )}

      {status && (
        <div className={`text-sm rounded-md px-3 py-2 flex items-start gap-2 border ${statusTone === "error" ? "text-red-700 bg-red-50 border-red-100" : statusTone === "success" ? "text-green-700 bg-green-50 border-green-100" : "text-gray-700 bg-gray-50 border-gray-200"}`}>
          <i className={`fa-solid mt-0.5 ${statusTone === "error" ? "fa-circle-exclamation" : statusTone === "success" ? "fa-circle-check" : "fa-circle-info"}`} aria-hidden="true" />
          <span className="break-all">{status}</span>
        </div>
      )}

      <div className="text-sm font-semibold text-gray-800 pt-2">{t("formSettings.schemaHeader")}</div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-gray-700 border-b">
              <th className="py-2 pr-2 w-14">#</th>
              <th className="py-2 pr-2">{t("formSettings.colLabel")}</th>
              <th className="py-2 pr-2 w-32">{t("formSettings.colType")}</th>
              <th className="py-2 pr-2">{t("formSettings.colValues")}</th>
              <th className="py-2 pr-2">{t("formSettings.colDescription")}</th>
              <th className="py-2 pr-2 w-28 text-right">{t("formSettings.colActions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const isChoice = CHOICE_TYPES.has(r.type);
              const isTitle = r.type === "title";
              const isFormTitle = isTitle && i === 0;
              const labelPlaceholder = isFormTitle ? t("formSettings.titlePlaceholder") : isTitle ? t("formSettings.sectionTitlePlaceholder") : i === 1 ? ID_LABEL : "";
              return (
                <tr
                  key={i}
                  className={`border-b align-top ${
                    dragIndex !== null && dropIndex === i && dragIndex !== i ? "bg-indigo-50" : ""
                  } ${dragIndex === i ? "opacity-50" : ""}`}
                  onDragOver={(e) => {
                    if (locked || dragIndex === null) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (dropIndex !== i) setDropIndex(i);
                  }}
                  onDrop={(e) => {
                    if (locked || dragIndex === null) return;
                    e.preventDefault();
                    handleDrop(i);
                  }}
                >
                  <td className="py-2 pr-2 text-gray-500">
                    <div className="flex items-center gap-1.5">
                      {!locked && (
                        <span
                          draggable
                          onDragStart={(e) => {
                            setDragIndex(i);
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onDragEnd={() => {
                            setDragIndex(null);
                            setDropIndex(null);
                          }}
                          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                          title={t("formSettings.dragToReorder")}
                          aria-label={t("formSettings.dragToReorder")}
                        >
                          <i className="fa-solid fa-grip-vertical" aria-hidden="true" />
                        </span>
                      )}
                      <span>{i + 1}</span>
                    </div>
                  </td>
                  <td className="py-2 pr-2">
                    <input type="text" value={r.label} onChange={(e) => updateEditorRow(i, { label: e.target.value })} disabled={locked} className={inputCls} placeholder={labelPlaceholder} />
                  </td>
                  <td className="py-2 pr-2">
                    <select value={r.type} onChange={(e) => updateEditorRow(i, { type: e.target.value })} disabled={locked} className={inputCls}>
                      {FORM_TYPES.map((tp) => <option key={tp} value={tp}>{t(`formSettings.type.${tp}`)}</option>)}
                    </select>
                  </td>
                  <td className="py-2 pr-2">
                    <input type="text" value={r.values} onChange={(e) => updateEditorRow(i, { values: e.target.value })} disabled={locked || !isChoice} className={inputCls} placeholder={isChoice ? t("formSettings.valuesPlaceholder") : ""} />
                  </td>
                  <td className="py-2 pr-2">
                    <textarea value={r.description} onChange={(e) => updateEditorRow(i, { description: e.target.value })} disabled={locked} rows={2} className={`${inputCls} resize-y`} placeholder={isFormTitle ? t("formSettings.formDescriptionPlaceholder") : isTitle ? t("formSettings.sectionDescriptionPlaceholder") : t("formSettings.descriptionPlaceholder")} />
                  </td>
                  <td className="py-2 pr-2">
                    <div className="flex items-center justify-end gap-1">
                      <button type="button" onClick={() => moveRow(i, -1)} disabled={locked || i === 0} className="px-2 py-1 text-gray-600 hover:text-gray-900 disabled:opacity-30" title={t("formSettings.moveUp")}><i className="fa-solid fa-arrow-up" aria-hidden="true" /></button>
                      <button type="button" onClick={() => moveRow(i, 1)} disabled={locked || i === rows.length - 1} className="px-2 py-1 text-gray-600 hover:text-gray-900 disabled:opacity-30" title={t("formSettings.moveDown")}><i className="fa-solid fa-arrow-down" aria-hidden="true" /></button>
                      <button type="button" onClick={() => removeRow(i)} disabled={locked} className="px-2 py-1 text-red-600 hover:text-red-800 disabled:opacity-30" title={t("formSettings.remove")}><i className="fa-solid fa-trash" aria-hidden="true" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!locked && (
        <button type="button" onClick={addRow} className="inline-flex items-center gap-2 text-sm text-indigo-700 hover:text-indigo-900">
          <i className="fa-solid fa-plus" aria-hidden="true" />
          <span>{t("formSettings.addField")}</span>
        </button>
      )}

      {locked ? (
        <button className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 text-white rounded-md px-3 py-2 hover:bg-indigo-700 disabled:opacity-50 transition-colors" onClick={startEdit} disabled={busy || !spreadsheetId}>
          <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
          <span>{t("formSettings.edit")}</span>
        </button>
      ) : (
        <div className="flex gap-2">
          <button className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 text-white rounded-md px-3 py-2 hover:bg-emerald-700 disabled:opacity-50 transition-colors" onClick={handleSaveSchema} disabled={busy || !spreadsheetId}>
            <i className="fa-solid fa-floppy-disk" aria-hidden="true" />
            <span>{t("formSettings.saveSchema")}</span>
          </button>
          <button className="inline-flex items-center justify-center gap-2 bg-gray-200 text-gray-700 rounded-md px-3 py-2 hover:bg-gray-300 disabled:opacity-50 transition-colors" onClick={cancelEdit} disabled={busy}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
            <span>{t("formSettings.cancel")}</span>
          </button>
        </div>
      )}

      <div className="text-xs text-gray-500 pt-1">{t("formSettings.schemaInfoNote")}</div>

      {spreadsheetId && (
        <div className="text-xs text-gray-600 pt-2">
          <a href={spreadsheetUrl(spreadsheetId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 hover:underline">
            <i className="fa-solid fa-up-right-from-square" aria-hidden="true" />
            <span>{t("sheet.openGoogleSheet")}</span>
          </a>
        </div>
      )}
    </div>
    </>
  );
}
